/**
 * The medicine pickers' options (#690): the Facility's drug Products from its
 * Warehouse, then registry medicines. Hook-free, so the form's tests reach it
 * without the organization context.
 */
import { translateKey } from 'src/locales/translate';
import {
  GetMedicineDto,
  WarehouseMedicineProductDto,
  WarehouseMedicineSearchDto,
} from 'woofwoof-api';

export interface WarehouseMedicineOption {
  source: 'WAREHOUSE';
  product: WarehouseMedicineProductDto;
  /** The registry medicine the Product links to; null for a manual Product. */
  medicine: GetMedicineDto | null;
  /** `YYYY-MM-DD` of the Batch FEFO takes next, or null. */
  nextExpiryDate: string | null;
}

export interface RegistryMedicineOption {
  source: 'REGISTRY';
  medicine: GetMedicineDto;
}

export type MedicineSearchOption =
  | WarehouseMedicineOption
  | RegistryMedicineOption;

/**
 * The most Warehouse Products one answer lists: the backend's
 * `WAREHOUSE_MEDICINE_SEARCH_WAREHOUSE_LIMIT`.
 */
export const WAREHOUSE_OPTIONS_CAP = 10;

/**
 * Below the cap the Warehouse group lists every Product that matched, so a
 * registry option is one the search found no Product for (R13).
 */
export function warehouseGroupIsComplete(
  options: MedicineSearchOption[],
): boolean {
  const listed = options.filter((option) => option.source === 'WAREHOUSE');
  return listed.length < WAREHOUSE_OPTIONS_CAP;
}

export function registryOptions(
  medicines: GetMedicineDto[],
): MedicineSearchOption[] {
  return medicines.map(
    (medicine): MedicineSearchOption => ({ source: 'REGISTRY', medicine }),
  );
}

/** The merged answer as one list, Warehouse options first (the server's order). */
export function toMedicineSearchOptions(
  result: WarehouseMedicineSearchDto,
): MedicineSearchOption[] {
  return [
    ...result.warehouse.map(
      (option): MedicineSearchOption => ({
        source: 'WAREHOUSE',
        product: option.product,
        medicine: option.medicine,
        nextExpiryDate: option.nextExpiryDate,
      }),
    ),
    ...registryOptions(result.registry),
  ];
}

/** The name the form records: the registry's for a linked Product, else the Product's own. */
export function medicineOptionName(option: MedicineSearchOption): string {
  if (option.source === 'REGISTRY') {
    return option.medicine.productName;
  }
  return option.medicine?.productName ?? option.product.name;
}

export function medicineOptionLabel(
  option: MedicineSearchOption | string,
): string {
  return typeof option === 'string' ? option : medicineOptionName(option);
}

export function medicineOptionKey(option: MedicineSearchOption): string {
  return option.source === 'WAREHOUSE'
    ? `warehouse-${option.product.id}`
    : `registry-${option.medicine.id}`;
}

export function medicineOptionGroup(
  option: MedicineSearchOption | string,
): string {
  if (typeof option === 'string') {
    return '';
  }
  return translateKey(
    option.source === 'WAREHOUSE'
      ? 'warehouse.medicineSearch.groupWarehouse'
      : 'warehouse.medicineSearch.groupRegistry',
  );
}

/** The form holds the recorded name, so an option matches it by that name. */
export function isSameMedicineOption(
  option: MedicineSearchOption | string,
  value: MedicineSearchOption | string,
): boolean {
  if (typeof option === 'string' || typeof value === 'string') {
    return medicineOptionLabel(option) === medicineOptionLabel(value);
  }
  return medicineOptionKey(option) === medicineOptionKey(value);
}

/** The server already matched (a Product may match only through its registry link). */
export function keepServerOrder<T>(options: T[]): T[] {
  return options;
}

/** Stock at or below zero reads in the error colour. */
export function optionStockColor(
  product: Pick<WarehouseMedicineProductDto, 'onHandMilli'>,
): 'error' | 'text.secondary' {
  return product.onHandMilli <= 0 ? 'error' : 'text.secondary';
}

/** "03.2027" of a `YYYY-MM-DD` date: "ważn. do MM.RRRR". */
export function formatExpiryMonth(date: string): string {
  const [year, month] = date.split('-');
  return `${month}.${year}`;
}
